import { FC } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';

import { Interval, SharesTitle } from 'types';

import { dateFormat } from 'constants/formats';
import { formatNumber } from 'utils/formats';
import { getShareTitleWatermarkText } from 'utils/getShareTitleWatermarkText';

import DocumentsColumn from 'modules/_shared/components/DocumentsColumn';
import ToggleMenu from 'modules/_shared/components/ToggleMenu';
import { generateSharesTitlesFilename } from 'modules/partners/utils/generateSharesTitlesFilename';
import { RowCell } from 'modules/_shared/Table/types/RowCell';
import TableRow from 'modules/_shared/Table/components/TableRow';

import { SocietyCorporateId } from 'types/Society';
import { useShareTitleListPage } from '../context/ShareTitleListPageContext';
import { useGetDataFromTitle } from '../hooks/useGetDataFromTitle';
import { getMarginSeparator } from '../utils/getMarginSeparator';
import { PartnerRow } from '../types/PartnerRow';
import IntervalRowLine from './IntervalRowLine';
import PartnerRowLine from './PartnerRowLine';

type Props = {
  index: number;
  title: SharesTitle;
};

const ShareTitleTableRow: FC<Props> = ({ index, title }) => {
  const { t } = useTranslate();

  const { data, actions } = useShareTitleListPage();
  const { data: shareTitleData } = useGetDataFromTitle({ title });

  const downloadTitleName = title
    ? generateSharesTitlesFilename({ sharesTitle: title, t })
    : '';

  const watermarkText = getShareTitleWatermarkText({
    status: title.status,
    isAdmin: data.isAdmin,
  });

  const handleChageTitleStatus = () => {
    actions.handleEdit(index);
  };

  const hasOptions = data.isAdmin && !data.isReadOnly;
  const hasSubtitle = data.corporateId === SocietyCorporateId.AXA;
  const hasPartners =
    shareTitleData.partners && shareTitleData.partners.length > 0;

  const toggleOptions = [
    <a
      className="d-flex cursor-pointer"
      key={`title-status-option-${index}`}
      onClick={handleChageTitleStatus}
    >
      <em className="icon ni ni-edit fs-20px" />
      <span className="ml-1">{t('ChangeStatus')}</span>
    </a>,
  ];

  const cells = [
    {
      key: `number-${index}`,
      content: title?.number,
      className: 'w-20px',
    },
    hasSubtitle
      ? {
          key: `subtitle-${index}`,
          content: title?.numberReplaced,
          className: 'w-20px',
          hideOnMobile: true,
        }
      : null,

    {
      key: `partner-${index}`,
      content: hasPartners
        ? shareTitleData.partners?.map((partner: PartnerRow) => (
            // eslint-disable-next-line react/jsx-indent
            <div key={partner.id}>
              <PartnerRowLine societyId={data.societyId} partner={partner} />
            </div>
          ))
        : '-',
    },
    {
      key: `operation-${index}`,
      content: shareTitleData.operationTypeLabel ? (
        <Link
          to={`/detalle-transacciones/${data.societyId}/${shareTitleData.operationId}`}
          className="text-primary fw-medium"
        >
          {t(shareTitleData.operationTypeLabel)}
        </Link>
      ) : (
        '-'
      ),
      className: 'w-150px',
      hideOnMobile: true,
    },

    {
      key: `intervals-${index}`,
      content: (
        <>
          {shareTitleData.intervals
            ?.slice(0, 3)
            .map((interval: Interval, intervalIndex: number) => (
              <div
                key={`${index}-${interval.shareFrom}-${interval.shareTo}`}
                style={getMarginSeparator(intervalIndex)}
              >
                <IntervalRowLine interval={interval} />
              </div>
            ))}
          {shareTitleData.intervals && shareTitleData.intervals.length > 5 && (
            <div className="mt-1 ff-italic text-muted fs-12px">
              ...
              {t('AndXNumerationsMore', {
                count: formatNumber(shareTitleData.intervals.length - 5),
              })}
            </div>
          )}
        </>
      ),
      textPosition: 'center',
      hideOnMobile: true,
      className: 'w-200px',
    },
    {
      key: `date-${index}`,
      content: title?.createdAt
        ? format(new Date(title.createdAt), dateFormat)
        : '-',
      textPosition: 'center',
      hideOnMobile: true,
    },
    {
      key: `status-${index}`,
      content: (
        <span className={`badge badge-dot badge-${shareTitleData.statusBadge}`}>
          {t(shareTitleData.statusLabel)}
        </span>
      ),
      textPosition: 'center',
    },
    {
      key: `document-${index}`,
      content: (
        <DocumentsColumn
          index={index}
          documentsCount={1}
          documents={
            data.societyShareTitleDocument
              ? [data.societyShareTitleDocument]
              : []
          }
          downloadName={downloadTitleName}
          hideOnMobile={false}
          sources={{ society: data.societyId, sharesTitle: title?._id }}
          templateWatermarkText={watermarkText}
          templateBackgroundImage={data.backgroundImage}
          templateTextPadding={data.textPadding}
          isLandscape={data.isLanscape}
        />
      ),
      documentCell: true,
    },

    hasOptions
      ? {
          key: `options-${index}`,
          content: <ToggleMenu toggleOptions={toggleOptions} />,
          textPosition: 'center',
        }
      : null,
  ].filter(Boolean) as RowCell[];

  return <TableRow cells={cells} />;
};

export default ShareTitleTableRow;
